<template>
  <a-layout :style="{ minHeight: '100vh' }">
    <!-- 侧边栏 -->
    <a-layout-sider
      v-if="settings.layout === 'sidemenu'"
      :style="siderStyle"
      :collapsed="collapsed"
      :width="effectiveSiderWidth"
      :collapsedWidth="effectiveCollapsedWidth"
      :theme="settings.theme"
      :collapsible="false"
      :breakpoint="'lg'"
    >
      <!-- Logo 区域 -->
      <div class="logo" v-if="!collapsed">
        <img src="~@/assets/logo1.png" alt="logo" />
        <h4>弘笃智慧云管理系统</h4>
      </div>
      <div class="logo-collapsed" v-else>
        <img src="~@/assets/logo1.png" alt="logo" />
      </div>
      
      <!-- 菜单 -->
      <a-menu
        ref="menuRef"
        :open-keys="state.openKeys"
        v-model:selectedKeys="state.selectedKeys"
        mode="inline"
        :theme="settings.theme"
        :inline-collapsed="collapsed"
        :triggerSubMenuAction="'click'"
        @click="handleMenuClick"
        @openChange="handleOpenChange"
      >
        <template v-for="(item, index) in menuItems">
          <!-- 有子菜单的情况 -->
          <a-sub-menu 
            v-if="item.children && item.children.length > 0" 
            :key="item.menuKey"
          >
            <template #icon v-if="getParentMenuIcon(item, index)">
              <img :src="getParentMenuIcon(item, index)" alt="" style="width: 16px; height: 16px; display: inline-block;" />
            </template>
            <template #title>{{ item.label }}</template>
            <template v-for="child in item.children">
              <a-sub-menu 
                v-if="child.children && child.children.length > 0" 
                :key="`sub-${child.menuKey}`"
              >
                <template #icon v-if="child.icon">
                  <img :src="child.icon" alt="" style="width: 16px; height: 16px; display: inline-block;" />
                </template>
                <template #title>{{ child.label }}</template>
                <a-menu-item 
                  v-for="grandChild in child.children" 
                  :key="grandChild.menuKey"
                  @click="handleMenuClick({ key: grandChild.menuKey })"
                >
                  <template #icon v-if="grandChild.icon">
                    <img :src="grandChild.icon" alt="" style="width: 16px; height: 16px; display: inline-block;" />
                  </template>
                  {{ grandChild.label }}
                </a-menu-item>
              </a-sub-menu>
              <a-menu-item 
                v-else 
                :key="child.menuKey"
                @click="handleMenuClick({ key: child.menuKey })"
              >
                <template #icon v-if="child.icon">
                  <img :src="child.icon" alt="" style="width: 16px; height: 16px; display: inline-block;" />
                </template>
                {{ child.label }}
              </a-menu-item>
            </template>
          </a-sub-menu>
          <!-- 没有子菜单的情况 -->
          <a-menu-item 
            v-else 
            :key="`top-${item.menuKey}`"
            @click="handleMenuClick({ key: item.menuKey })"
          >
            <template #icon v-if="item.icon">
              <img :src="item.icon" alt="" style="width: 16px; height: 16px; display: inline-block;" />
            </template>
            {{ item.label }}
          </a-menu-item>
        </template>
      </a-menu>
    </a-layout-sider>

    <!-- 主内容区域 -->
    <a-layout>
      <!-- 顶部导航 -->
      <a-layout-header :style="headerStyle">
        <div class="header-content">
          <!-- 折叠按钮 -->
          <a-button
            v-if="settings.layout === 'sidemenu'"
            type="text"
            @click="handleCollapse(!collapsed)"
            :style="{ fontSize: '16px', width: 64, height: 64 }"
          >
            <MenuUnfoldOutlined v-if="collapsed" />
            <MenuFoldOutlined v-else />
          </a-button>
          
          <!-- 右侧内容 -->
          <right-content
            :top-menu="settings.layout === 'topmenu'"
            :is-mobile="isMobile"
            :theme="settings.theme"
          />
        </div>
      </a-layout-header>

      <!-- 内容区域 -->
      <a-layout-content :style="contentStyle">
        <multi-tab v-if="settings.multiTab"></multi-tab>
        <router-view v-slot="{ Component }">
          <transition name="page-transition">
            <keep-alive :include="cachedViews">
              <component :is="Component" :key="key" />
            </keep-alive>
          </transition>
        </router-view>
      </a-layout-content>

      <!-- 底部 -->
      <!-- 暂时移除 GlobalFooter，因为它使用了 Vue 2 的 API，导致错误 -->
      <!-- <a-layout-footer :style="footerStyle" v-if="!hideFooter">
        <global-footer />
      </a-layout-footer> -->
    </a-layout>

    <!-- 设置抽屉已关闭：菜单风格写死暗色 -->
    <!-- <setting-drawer
      v-if="isProPreviewSite"
      :settings="settings"
      @change="handleSettingChange"
    /> -->
  </a-layout>
</template>

<script>
import { h, reactive } from 'vue'
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons-vue'
import MultiTab from '@/components/MultiTab'
import { updateTheme } from '@/components/ProLayout'
import { i18nRender } from '@/locales'
import { mapState } from 'vuex'
import {
  CONTENT_WIDTH_TYPE,
  SIDE_COLLAPSED,
  TOGGLE_MOBILE_TYPE,
  TOGGLE_CONTENT_WIDTH,
  TOGGLE_FIXED_HEADER,
  TOGGLE_FIXED_SIDEBAR,
  TOGGLE_LAYOUT,
  TOGGLE_NAV_THEME,
  TOGGLE_WEAK,
  TOGGLE_COLOR,
  TOGGLE_MULTI_TAB,
  TABLE_SIZE,
  TABLE_BORDERED,
  HIDE_FOOTER,
} from '@/store/mutation-types'

import defaultSettings from '@/config/defaultSettings'
import RightContent from '@/components/GlobalHeader/RightContent'
// import GlobalFooter from '@/components/GlobalFooter' // 暂时移除，因为使用了 Vue 2 的 API
import Ads from '@/components/Other/CarbonAds'
import { baseMixin } from '@/store/app-mixin'

export default {
  name: 'BasicLayout',
  components: {
    RightContent,
    // GlobalFooter, // 暂时移除
    Ads,
    MultiTab,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
  },
  mixins: [baseMixin],
  data() {
    return {
      // preview.pro.antdv.com only use.是否展示设置页面样式按钮；先写死 true;后续可根据 process.env.VUE_APP_PREVIEW === 'true', 进行控制
      isProPreviewSite: false,
      // end
      // base
      menus: [],
      // 侧栏展开状态
      collapsed: false,
      title: defaultSettings.title,
      // 菜单状态
      state: {
        openKeys: [],
        selectedKeys: [],
        // 记录手动操作的历史，防止路由变化覆盖手动展开的菜单
        manuallyOpenedKeys: new Set(),
        // 用于查找菜单项映射关系
        menuKeyToRouteMap: new Map(),
        // 记录用户手动选择的菜单项，防止被 updateMenuState 覆盖
        manuallySelectedKey: null,
      },
      settings: {
        // 布局类型
        layout: defaultSettings.layout, // 'sidemenu', 'topmenu'
        // CONTENT_WIDTH_TYPE
        contentWidth:
          defaultSettings.layout === 'sidemenu'
            ? CONTENT_WIDTH_TYPE.Fluid
            : defaultSettings.contentWidth,
        // 主题写死暗色，不再跟随本地缓存
        theme: 'dark',
        // 主色调
        primaryColor: defaultSettings.primaryColor,
        fixedHeader: defaultSettings.fixedHeader,
        fixSiderbar: defaultSettings.fixSiderbar,
        multiTab: defaultSettings.multiTab,
        colorWeak: defaultSettings.colorWeak,

        hideHintAlert: true,
        hideCopyButton: false,
        tableSize: defaultSettings.tableSize,
        tableBordered: defaultSettings.tableBordered,
        hideFooter: defaultSettings.hideFooter,

        // 默认侧边栏宽度（会在大屏自动增强）
        siderWidth: defaultSettings.siderWidth || 256,
      },
      // 媒体查询
      query: {},
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    }
  },
  computed: {
    ...mapState({
      // 动态主路由
      mainMenu: (state) => state.permission.menus,
    }),
    cachedViews() {
      return this.$store.state.tagsView.cachedViews
    },
    key() {
      return this.$route.path
    },
    hideFooter() {
      return this.settings.hideFooter
    },
    isUltraWideScreen() {
      // Windows 缩放会让 CSS 像素 < 物理像素，使用 devicePixelRatio 还原物理宽度
      const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1
      const screenCssW = typeof window !== 'undefined' ? (window.screen?.width || 0) : 0
      const physicalW = Math.round(Math.max(this.viewportWidth, screenCssW) * dpr)
      return physicalW >= 2560
    },
    effectiveSiderWidth() {
      const base = Number(this.settings.siderWidth) || 256
      // 2560×1600 等超宽屏：按需求固定为 200px
      if (this.isUltraWideScreen) return 200
      return base
    },
    effectiveCollapsedWidth() {
      // 折叠态保持统一体验
      return 80
    },
    siderStyle() {
      return {
        overflow: 'auto',
        overflowX: 'hidden',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
      }
    },
    headerStyle() {
      const siderW = this.effectiveSiderWidth
      const collapsedW = this.effectiveCollapsedWidth
      return {
        padding: '0',
        background: '#fff',
        position: this.settings.fixedHeader ? 'fixed' : 'static',
        top: 0,
        right: 0,
        left:
          this.settings.layout === 'sidemenu' && !this.collapsed
            ? siderW + 'px'
            : this.settings.layout === 'sidemenu' && this.collapsed
              ? collapsedW + 'px'
              : '0',
        zIndex: 9,
        width:
          this.settings.layout === 'sidemenu' && !this.collapsed
            ? `calc(100% - ${siderW}px)`
            : this.settings.layout === 'sidemenu' && this.collapsed
              ? `calc(100% - ${collapsedW}px)`
              : '100%',
        transition: 'all 0.2s',
      }
    },
    contentStyle() {
      const siderW = this.effectiveSiderWidth
      const collapsedW = this.effectiveCollapsedWidth
      return {
        margin: this.settings.fixedHeader ? '64px 0 0' : '0',
        padding: '24px',
        minHeight: 'calc(100vh - 64px)',
        background: '#f0f2f5',
        marginLeft:
          this.settings.layout === 'sidemenu' && !this.collapsed
            ? siderW + 'px'
            : this.settings.layout === 'sidemenu' && this.collapsed
              ? collapsedW + 'px'
              : '0',
        transition: 'all 0.2s',
      }
    },
    footerStyle() {
      const siderW = this.effectiveSiderWidth
      const collapsedW = this.effectiveCollapsedWidth
      return {
        textAlign: 'center',
        background: '#fff',
        padding: '16px 50px',
        marginLeft:
          this.settings.layout === 'sidemenu' && !this.collapsed
            ? siderW + 'px'
            : this.settings.layout === 'sidemenu' && this.collapsed
              ? collapsedW + 'px'
              : '0',
        transition: 'all 0.2s',
      }
    },
    // 将路由数据转换为菜单项格式
    menuItems() {
      const items = this.convertMenusToItems(this.menus)
      return items || []
    },
  },
  created() {
    console.log('[菜单调试] created - mainMenu:', {
      type: typeof this.mainMenu,
      isArray: Array.isArray(this.mainMenu),
      length: this.mainMenu ? this.mainMenu.length : 0,
      data: this.mainMenu
    })
    this.updateMenus()
    
    // 监听菜单数据变化，自动更新
    this.$watch('mainMenu', (newVal) => {
      console.log('[菜单调试] mainMenu changed:', {
        length: newVal ? newVal.length : 0,
        data: newVal
      })
      this.updateMenus()
    }, { deep: true, immediate: false })
    
    // 处理侧栏展开状态
    this.$watch('collapsed', (val) => {
      this.$store.commit(SIDE_COLLAPSED, val)
      if (val) {
        // 折叠时保存当前的 openKeys 到缓存，以便展开时恢复
        this.state.cachedOpenKeysForCollapse = [...this.state.openKeys]
        this.state.openKeys = []
      } else {
        // 展开时恢复之前展开的菜单
        // 优先使用手动展开的菜单，如果没有则使用路由决定的菜单
        let keysToRestore = []
        if (this.state.manuallyOpenedKeys.size > 0) {
          keysToRestore = Array.from(this.state.manuallyOpenedKeys)
        } else if (this.state.cachedOpenKeysForCollapse && this.state.cachedOpenKeysForCollapse.length > 0) {
          keysToRestore = this.state.cachedOpenKeysForCollapse
        } else {
          keysToRestore = this.getRouteOpenKeys()
        }
        this.state.openKeys = keysToRestore
      }
    })
    
    // 监听路由变化，更新菜单选中状态
    this.$watch('$route', () => {
      this.updateMenuState()
    })
    
  },
  mounted() {
    this.settings.theme = 'dark'
    if (this.isProPreviewSite) {
      this.settings.layout = this.layout
      this.settings.contentWidth = this.contentWidth
      this.settings.primaryColor = this.primaryColor
      this.settings.fixedHeader = this.fixedHeader
      this.settings.fixSiderbar = this.fixedSidebar
      this.settings.multiTab = this.multiTab
      this.settings.colorWeak = this.colorWeak
      this.settings.tableSize = this.tableSize
      this.settings.tableBordered = this.tableBordered
      this.settings.hideFooter = this.hideFooter
    }
    this.collapsed = this.sideCollapsed
    
    const userAgent = navigator.userAgent
    if (userAgent.indexOf('Edge') > -1) {
      this.$nextTick(() => {
        this.collapsed = !this.collapsed
        setTimeout(() => {
          this.collapsed = !this.collapsed
        }, 16)
      })
    }

    // first update color
    // TIPS: THEME COLOR HANDLER!! PLEASE CHECK THAT!!
    if (
      process.env.NODE_ENV !== 'production' ||
      process.env.VUE_APP_PREVIEW === 'true'
    ) {
      updateTheme(this.settings.primaryColor)
    }
    
    // 初始化菜单选中状态
    this.updateMenuState()
    
    // 确保在 mounted 时也更新菜单（因为数据可能此时才加载完成）
    this.$nextTick(() => {
      console.log('[菜单调试] mounted nextTick - mainMenu:', {
        length: this.mainMenu ? this.mainMenu.length : 0,
        data: this.mainMenu
      })
      this.updateMenus()
    })
    
    // 动态插入样式
    this.injectCustomStyles()

    // 监听视口尺寸变化，用于大屏侧边栏宽度自适应
    this.updateViewportWidth()
    window.addEventListener('resize', this.updateViewportWidth, { passive: true })
  },
  beforeUnmount() {
    // 清理动态插入的样式
    this.removeCustomStyles()
    window.removeEventListener('resize', this.updateViewportWidth)
  },
  methods: {
    updateViewportWidth() {
      const innerW = window.innerWidth || 0
      const clientW = document?.documentElement?.clientWidth || 0
      const screenW = window.screen?.width || 0
      // 取更稳定的最大值，避免由于滚动条/缩放/浏览器 UI 造成误判
      this.viewportWidth = Math.max(innerW, clientW, screenW)
    },
    /** 获取父菜单图标 */
    getParentMenuIcon(item, index) {
      // 只处理有子菜单的父菜单
      if (!item.children || item.children.length === 0) {
        return item.icon
      }
      
      // 根据索引返回对应的图标
      let iconPath = null
      if (index === 0) {
        // 第一个父菜单使用 1.png
        iconPath = require('@/assets/images/1.png')
      } else if (index === 1) {
        // 第二个父菜单使用 2.png
        iconPath = require('@/assets/images/2.png')
      } else if (index === 2) {
        // 第三个父菜单使用 6.png
        iconPath = require('@/assets/images/6.png')
      } else if (index === 3) {
        // 第四个父菜单使用 1.png
        iconPath = require('@/assets/images/1.png')
      } else {
        // 其他的照常使用 item.icon
        return item.icon
      }
      
      return iconPath
    },
    // 动态注入自定义样式
    injectCustomStyles() {
      const styleId = 'custom-menu-styles'
      if (document.getElementById(styleId)) {
        return
      }
      
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        /* 强制覆盖 Ant Design 的菜单选中样式（包括所有嵌套层级） */
        .ant-menu-inline .ant-menu-item-selected,
        .ant-menu-vertical .ant-menu-item-selected,
        .ant-menu-vertical-left .ant-menu-item-selected,
        .ant-menu-vertical-right .ant-menu-item-selected,
        .ant-menu .ant-menu-item-selected,
        .ant-menu .ant-menu .ant-menu-item-selected,
        .ant-menu .ant-menu .ant-menu .ant-menu-item-selected,
        li.ant-menu-item-selected,
        li.ant-menu-item-selected > a,
        .ant-menu-item-selected > span,
        .ant-menu-item-selected > div {
          background-color: #165DFF !important;
          color: #ffffff !important;
        }
        
        .ant-menu-inline .ant-menu-item-selected .anticon,
        .ant-menu-vertical .ant-menu-item-selected .anticon,
        .ant-menu-vertical-left .ant-menu-item-selected .anticon,
        .ant-menu-vertical-right .ant-menu-item-selected .anticon,
        .ant-menu .ant-menu-item-selected .anticon,
        .ant-menu .ant-menu-item-selected img {
          color: #ffffff !important;
        }
        
        .ant-menu-inline .ant-menu-item-selected:hover,
        .ant-menu-vertical .ant-menu-item-selected:hover,
        .ant-menu-vertical-left .ant-menu-item-selected:hover,
        .ant-menu-vertical-right .ant-menu-item-selected:hover,
        .ant-menu .ant-menu-item-selected:hover {
          background-color: #165DFF !important;
          color: #ffffff !important;
        }
        
        /* 移除 Ant Design 默认的选中边框 */
        .ant-menu-inline .ant-menu-item-selected::after,
        .ant-menu-vertical .ant-menu-item-selected::after,
        .ant-menu-vertical-left .ant-menu-item-selected::after,
        .ant-menu-vertical-right .ant-menu-item-selected::after,
        .ant-menu .ant-menu-item-selected::after {
          display: none !important;
          opacity: 0 !important;
        }
        
        /* 浅色主题的菜单样式 */
        .ant-menu-light .ant-menu-item-selected {
          background-color: #165DFF !important;
          color: #ffffff !important;
        }
        
        .ant-menu-light .ant-menu-item-selected .anticon,
        .ant-menu-light .ant-menu-item-selected img {
          color: #ffffff !important;
        }
        
        /* 深色主题的菜单样式 */
        .ant-menu-dark .ant-menu-item-selected {
          background-color: #165DFF !important;
          color: #ffffff !important;
        }
        
        .ant-menu-dark .ant-menu-item-selected .anticon,
        .ant-menu-dark .ant-menu-item-selected img {
          color: #ffffff !important;
        }
        
        /* 子菜单中的激活项 - 更具体的选择器 */
        .ant-menu-submenu .ant-menu-item-selected,
        .ant-menu-submenu .ant-menu .ant-menu-item-selected,
        .ant-menu-submenu .ant-menu-inline .ant-menu-item-selected,
        .ant-menu .ant-menu-submenu .ant-menu-item-selected,
        .ant-menu-inline .ant-menu-submenu .ant-menu-item-selected,
        .ant-menu-submenu .ant-menu-submenu .ant-menu-item-selected,
        .ant-menu-submenu .ant-menu-item.ant-menu-item-selected,
        .ant-menu-submenu ul .ant-menu-item-selected,
        .ant-menu-submenu ul li.ant-menu-item-selected {
          background-color: #165DFF !important;
          background: #165DFF !important;
          color: #ffffff !important;
        }
        
        .ant-menu-submenu .ant-menu-item-selected .anticon,
        .ant-menu-submenu .ant-menu-item-selected img,
        .ant-menu-submenu .ant-menu .ant-menu-item-selected .anticon,
        .ant-menu-submenu .ant-menu .ant-menu-item-selected img,
        .ant-menu .ant-menu-submenu .ant-menu-item-selected .anticon,
        .ant-menu .ant-menu-submenu .ant-menu-item-selected img {
          color: #ffffff !important;
        }
        
        /* 子菜单激活项悬停 */
        .ant-menu-submenu .ant-menu-item-selected:hover,
        .ant-menu-submenu .ant-menu .ant-menu-item-selected:hover {
          background-color: #165DFF !important;
          color: #ffffff !important;
        }
        
        /* 移除子菜单激活项的边框 */
        .ant-menu-submenu .ant-menu-item-selected::after,
        .ant-menu-submenu .ant-menu .ant-menu-item-selected::after {
          display: none !important;
          opacity: 0 !important;
        }
        
        /* 菜单悬停效果 */
        .ant-menu-light .ant-menu-item:not(.ant-menu-item-selected):hover {
          background-color: rgba(22, 93, 255, 0.08) !important;
          color: #165DFF !important;
        }
        
        .ant-menu-light .ant-menu-item:not(.ant-menu-item-selected):hover .anticon {
          color: #165DFF !important;
        }
        
        .ant-menu-dark .ant-menu-item:not(.ant-menu-item-selected):hover {
          background-color: rgba(22, 93, 255, 0.2) !important;
          color: #ffffff !important;
        }
        
        .ant-menu-dark .ant-menu-item:not(.ant-menu-item-selected):hover .anticon {
          color: #ffffff !important;
        }
        
        /* 菜单项的圆角和间距 */
        .ant-menu-item, 
        .ant-menu-submenu-title {
          border-radius: 4px !important;
          margin: 2px 8px !important;
          transition: all 0.2s ease !important;
        }
        
        /* 强制激活状态样式 - 使用最高优先级 */
        .ant-menu-item.ant-menu-item-selected,
        .ant-menu-item.ant-menu-item-selected > span,
        .ant-menu-item.ant-menu-item-selected > a,
        .ant-menu-item.ant-menu-item-selected > div {
          background-color: #165DFF !important;
          background: #165DFF !important;
          color: #ffffff !important;
        }
        
        /* 子菜单中的激活项 - 最高优先级 */
        .ant-menu-submenu .ant-menu-item.ant-menu-item-selected,
        .ant-menu-submenu .ant-menu-item.ant-menu-item-selected > span,
        .ant-menu-submenu .ant-menu-item.ant-menu-item-selected > a,
        .ant-menu-submenu .ant-menu-item.ant-menu-item-selected > div {
          background-color: #165DFF !important;
          background: #165DFF !important;
          color: #ffffff !important;
        }
        
        /* 子菜单内部的菜单项 */
        .ant-menu-submenu .ant-menu-item {
          margin-left: 8px !important;
          margin-right: 8px !important;
        }
        
        .ant-menu-submenu .ant-menu-submenu-title {
          margin-left: 8px !important;
          margin-right: 8px !important;
        }
      `
      
      document.head.appendChild(style)
      console.log('动态样式已注入')
    },
    
    // 移除动态样式
    removeCustomStyles() {
      const styleId = 'custom-menu-styles'
      const styleElement = document.getElementById(styleId)
      if (styleElement) {
        document.head.removeChild(styleElement)
        console.log('动态样式已移除')
      }
    },
    
    // 处理菜单展开/收起变化
    handleOpenChange(openKeys) {
      // 记录用户手动操作：将当前所有展开的键添加到手动记录中
      openKeys.forEach(key => {
        this.state.manuallyOpenedKeys.add(key)
      })
      
      // 从手动记录中移除已收起的键
      const currentOpenKeysSet = new Set(openKeys)
      Array.from(this.state.manuallyOpenedKeys).forEach(key => {
        if (!currentOpenKeysSet.has(key)) {
          this.state.manuallyOpenedKeys.delete(key)
        }
      })
      
      // 更新 openKeys
      this.state.openKeys = openKeys
    },
    
    updateMenus() {
      console.log('[菜单调试] updateMenus - mainMenu:', {
        isArray: Array.isArray(this.mainMenu),
        length: this.mainMenu ? this.mainMenu.length : 0
      })
      // 尝试多种方式获取菜单数据
      let menus = []
      
      if (Array.isArray(this.mainMenu) && this.mainMenu.length > 0) {
        // 方式1: 查找 path === '/' 的路由
        const rootRoute = this.mainMenu.find((item) => item.path === '/')
        if (rootRoute && rootRoute.children) {
          // 过滤掉"首页"菜单
          menus = rootRoute.children.filter((item) => {
            const meta = item.meta || {}
            const title = meta.title || ''
            return title !== '首页' && item.path !== '/index' && item.name !== 'Index'
          })
          console.log('[菜单调试] 使用方式1: rootRoute.children, 数量:', menus.length)
        } else {
          // 方式2: 查找第一个有 children 的路由
          const routeWithChildren = this.mainMenu.find((item) => item.children && item.children.length > 0)
          if (routeWithChildren && routeWithChildren.children) {
            // 过滤掉"首页"菜单
            menus = routeWithChildren.children.filter((item) => {
              const meta = item.meta || {}
              const title = meta.title || ''
              return title !== '首页' && item.path !== '/index' && item.name !== 'Index'
            })
            console.log('[菜单调试] 使用方式2: routeWithChildren.children, 数量:', menus.length)
          } else {
            // 方式3: 如果 mainMenu 本身就是菜单数组，直接使用
            // 过滤掉 hidden 的路由和登录/404等系统路由以及"首页"菜单
            menus = this.mainMenu.filter((item) => {
              const meta = item.meta || {}
              const title = meta.title || ''
              return !item.hidden && 
                     item.path !== '/user' && 
                     item.path !== '/404' &&
                     item.path !== '/403' &&
                     item.path !== '/500' &&
                     title !== '首页' &&
                     item.path !== '/index' &&
                     item.name !== 'Index'
            })
            console.log('[菜单调试] 使用方式3: filtered mainMenu, 数量:', menus.length)
          }
        }
      } else {
        console.warn('[菜单调试] mainMenu 不是数组或为空:', this.mainMenu)
      }
      
      this.menus = menus
      console.log('[菜单调试] menus 更新完成:', {
        count: this.menus.length,
        firstItem: this.menus.length > 0 ? this.menus[0] : null
      })
      
      // 清空旧的映射关系
      this.state.menuKeyToRouteMap.clear()
      
      // 更新菜单状态
      this.$nextTick(() => {
        this.updateMenuState()
      })
    },
    
    // 将路由数据转换为 a-menu 的 items 格式
    convertMenusToItems(menus, parentIndex = 0) {
      console.log('[菜单调试] convertMenusToItems - 输入:', {
        isArray: Array.isArray(menus),
        length: menus ? menus.length : 0
      })
      if (!Array.isArray(menus)) {
        console.warn('[菜单调试] convertMenusToItems: menus 不是数组')
        return []
      }
      
      if (menus.length === 0) {
        console.warn('[菜单调试] convertMenusToItems: menus 为空数组')
        return []
      }
      
      let menuIndex = parentIndex
      
      const result = menus
        .filter(item => {
          // 过滤掉隐藏的菜单
          if (item.hidden) return false
          // 过滤掉"首页"菜单
          const meta = item.meta || {}
          const title = meta.title || ''
          const path = item.path || ''
          if (title === '首页' || path === '/index' || item.name === 'Index') {
            return false
          }
          return true
        })
        .map(item => {
          const hasChildren = item.children && item.children.length > 0 && !item.hideChildrenInMenu
          const meta = item.meta || {}
          const title = this.i18nRender(meta.title) || meta.title || item.name || ''
          
          // 处理图标 - 仅为一级父菜单分配图片；子菜单不展示图标，
          // 否则像「人员轨迹」这种能解析出 Ant 图标的项会多出 16px 缩进
          let icon = null
          if (parentIndex === 0) {
            const imageIndex = (menuIndex % 6) + 1 // 1-6 循环
            icon = require(`@/assets/images/${imageIndex}.png`)
            menuIndex++
          }
          
          // 创建唯一的菜单键
          // 优先使用 name，因为 name 在 Vue Router 中是唯一的
          const menuKey = String(item.name || item.path || `menu-${Date.now()}-${Math.random()}`)
          
          const menuItem = {
            menuKey: menuKey,
            label: title || '未命名菜单',
            title: title || '未命名菜单',
            ...(icon && { icon }),
            // 保存路由信息用于点击事件
            routeItem: item
          }
          
          // 存储映射关系：menuKey -> routeItem
          this.state.menuKeyToRouteMap.set(menuKey, item)
          
          // 如果有子菜单且不隐藏子菜单
          if (hasChildren) {
            const childrenItems = this.convertMenusToItems(item.children, parentIndex + 1)
            // 确保 children 是数组
            menuItem.children = Array.isArray(childrenItems) ? childrenItems : []
          } else {
            // 如果是外部链接
            const httpReg = /(http|https|ftp):\/\/([\w.]+\/?)\S*/
            const target = meta.target || null
            const hasRemoteUrl = httpReg.test(item.path)
            
            if (hasRemoteUrl || target) {
              // 对于外部链接，使用 h 函数渲染
              menuItem.label = () => {
                return h('a', {
                  href: item.path,
                  target: target || '_blank'
                }, title)
              }
            }
          }
          
          return menuItem
        })
      
      if (result.length > 0) {
        const firstItem = result[0]
        console.log('[菜单调试] convertMenusToItems - 输出:', {
          count: result.length,
          firstItem: {
            menuKey: firstItem.menuKey,
            label: typeof firstItem.label === 'function' ? '[Function]' : firstItem.label,
            labelType: typeof firstItem.label,
            icon: firstItem.icon ? (typeof firstItem.icon === 'function' ? '[Function]' : (firstItem.icon.name || firstItem.icon.__name || (firstItem.icon.type || '[VNode]'))) : null,
            iconType: firstItem.icon ? typeof firstItem.icon : 'null',
            hasChildren: !!firstItem.children,
            childrenCount: firstItem.children ? firstItem.children.length : 0,
            childrenType: firstItem.children ? (Array.isArray(firstItem.children) ? 'Array' : typeof firstItem.children) : 'null'
          },
          allKeys: result.map(item => item.menuKey),
          menuKeyToRouteMapSize: this.state.menuKeyToRouteMap.size
        })
      } else {
        console.log('[菜单调试] convertMenusToItems - 输出: 空数组')
      }
      return result
    },
    
    // 处理菜单点击事件
    handleMenuClick({ key }) {
      // 记录用户手动选择的菜单项
      this.state.manuallySelectedKey = key
      
      // 立即更新选中状态
      this.state.selectedKeys = [key]
      
      // 通过映射关系查找对应的路由项
      const routeItem = this.state.menuKeyToRouteMap.get(key)
      
      if (routeItem && routeItem.path) {
        const httpReg = /(http|https|ftp):\/\/([\w.]+\/?)\S*/
        const hasRemoteUrl = httpReg.test(routeItem.path)
        
        // 如果是外部链接，不处理（由浏览器默认行为处理）
        if (hasRemoteUrl) {
          return
        }
        
        // 修复路径格式
        let path = routeItem.path
        if (typeof path === 'string') {
          path = path.replace(/\/+/g, '/')
          if (!path.startsWith('/') && !httpReg.test(path)) {
            path = '/' + path
          }
        }
        
        // 路由跳转（不修改 openKeys，子菜单保持展开）
        if (this.$router) {
          this.$router.push(path).then(() => {
            // 路由跳转成功后，确保选中状态正确
            this.$nextTick(() => {
              this.state.selectedKeys = [key]
              this.state.manuallySelectedKey = key
            })
          }).catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              console.error('路由跳转失败', err)
            }
          })
        }
      } else {
        console.warn('[菜单调试] 未找到对应的路由项，key:', key)
        // 尝试查找菜单项（备用方案）
        const findMenuItem = (items, targetKey) => {
          for (const item of items) {
            const itemKey = item.name || item.path
            if (itemKey === targetKey) {
              return item
            }
            if (item.children) {
              const found = findMenuItem(item.children, targetKey)
              if (found) return found
            }
          }
          return null
        }
        
        const backupRouteItem = findMenuItem(this.menus, key)
        if (backupRouteItem && backupRouteItem.path) {
          console.log('[菜单调试] 使用备用方案找到路由项:', backupRouteItem.path)
          
          // 修复路径格式
          let path = backupRouteItem.path
          if (typeof path === 'string') {
            path = path.replace(/\/+/g, '/')
            if (!path.startsWith('/')) {
              path = '/' + path
            }
          }
          
          // 更新选中状态
          this.state.selectedKeys = [key]
          
          if (this.$router) {
            this.$router.push(path).catch(err => {
              if (err.name !== 'NavigationDuplicated') {
                console.error('路由跳转失败', err)
              }
            })
          }
        } else {
          console.error('[菜单调试] 未找到对应的菜单项，无法跳转')
        }
      }
    },
    
    // 获取当前路由对应的展开菜单键
    getRouteOpenKeys() {
      const route = this.$route
      if (!route || !route.meta) {
        return []
      }
      
      const names = route.meta.names || []
      const routeOpenKeys = []
      names.forEach(item => {
        if (item) {
          routeOpenKeys.push(item)
        }
      })
      
      return routeOpenKeys
    },
    
    // 更新菜单选中和展开状态
    updateMenuState() {
      const route = this.$route
      if (!route || !route.meta) {
        return
      }
      
      const names = route.meta.names || []
      const { hidden } = route.meta
      
      if (names.length >= 5 && hidden) {
        names.pop()
      }
      
      // 更新选中状态：使用当前路由的 name
      // 如果用户刚刚手动选择了菜单项，优先使用用户选择的
      if (this.state.manuallySelectedKey) {
        this.state.selectedKeys = [this.state.manuallySelectedKey]
        // 清除手动选择标志，让后续的路由变化可以自动更新
        setTimeout(() => {
          this.state.manuallySelectedKey = null
        }, 100)
        return
      }
      
      if (route.name) {
        // 查找对应的 menuKey
        let menuKey = null
        // 尝试从映射关系中查找
        for (const [key, value] of this.state.menuKeyToRouteMap.entries()) {
          if (value.name === route.name) {
            menuKey = key
            break
          }
        }
        
        // 如果没找到，尝试使用 route.name 作为 key
        if (!menuKey) {
          menuKey = route.name
          // 添加到映射关系中
          if (!this.state.menuKeyToRouteMap.has(menuKey)) {
            this.state.menuKeyToRouteMap.set(menuKey, { name: route.name, path: route.path })
          }
        }
        
        // 只有当找到有效的 menuKey 时才更新选中状态
        if (menuKey) {
          this.state.selectedKeys = [menuKey]
        }
      } else if (names.length > 0) {
        // 回退方案：使用 names 数组的最后一个
        this.state.selectedKeys = [names[names.length - 1]]
      }
      
      // 获取当前路由需要展开的菜单键
      const routeOpenKeys = this.getRouteOpenKeys()
      
      if (this.collapsed) {
        return
      }
      
      // 智能合并：保留用户手动展开的菜单，同时确保当前路由的父级菜单是展开的
      const currentOpenKeys = this.state.openKeys || []
      const manuallyOpenedKeys = Array.from(this.state.manuallyOpenedKeys)
      
      // 构建需要合并的键集合
      const keysToMerge = [...manuallyOpenedKeys, ...routeOpenKeys]
      
      // 只有当有新的需要展开的菜单时才更新
      const needsUpdate = keysToMerge.some(key => !currentOpenKeys.includes(key))
      
      if (needsUpdate) {
        // 创建新的 openKeys 集合（去重）
        const mergedOpenKeys = [...new Set(keysToMerge)]
        this.state.openKeys = mergedOpenKeys
        
        // 更新手动记录
        mergedOpenKeys.forEach(key => {
          this.state.manuallyOpenedKeys.add(key)
        })
      }
    },
    
    i18nRender,
    handleMediaQuery(val) {
      this.query = val
      if (this.isMobile && !val['screen-xs']) {
        this.$store.commit(TOGGLE_MOBILE_TYPE, false)
        return
      }
      if (!this.isMobile && val['screen-xs']) {
        this.$store.commit(TOGGLE_MOBILE_TYPE, true)
        this.collapsed = true
        this.settings.contentWidth = CONTENT_WIDTH_TYPE.Fluid
        // this.settings.fixSiderbar = false
      }
    },
    handleCollapse(val) {
      this.collapsed = typeof val === 'boolean' ? val : !this.collapsed
    },
    handleSettingChange({ type, value }) {
      type && (this.settings[type] = value)
      switch (type) {
        case 'theme':
          this.$store.commit(TOGGLE_NAV_THEME, value)
          break
        case 'primaryColor':
          this.$store.commit(TOGGLE_COLOR, value)
          break
        case 'contentWidth':
          this.settings[type] = value
          this.$store.commit(TOGGLE_CONTENT_WIDTH, value)
          break
        case 'layout':
          this.$store.commit(TOGGLE_LAYOUT, value)
          if (value === 'sidemenu') {
            this.settings.contentWidth = CONTENT_WIDTH_TYPE.Fluid
            this.$store.commit(TOGGLE_CONTENT_WIDTH, CONTENT_WIDTH_TYPE.Fluid)
          } else {
            this.settings.fixSiderbar = false
            this.$store.commit(TOGGLE_FIXED_SIDEBAR, false)
            this.settings.contentWidth = CONTENT_WIDTH_TYPE.Fixed
            this.$store.commit(TOGGLE_CONTENT_WIDTH, CONTENT_WIDTH_TYPE.Fixed)
          }
          break
        case 'fixSiderbar':
          this.$store.commit(TOGGLE_FIXED_SIDEBAR, value)
          break
        case 'fixedHeader':
          this.$store.commit(TOGGLE_FIXED_HEADER, value)
          break
        case 'multiTab':
          this.$store.commit(TOGGLE_MULTI_TAB, value)
          break
        case 'colorWeak':
          this.$store.commit(TOGGLE_WEAK, value)
          break
        case 'tableSize':
          this.$store.commit(TABLE_SIZE, value)
          break
        case 'tableBordered':
          this.$store.commit(TABLE_BORDERED, value)
          break
        case 'hideFooter':
          this.$store.commit(HIDE_FOOTER, value)
          break
      }
    },
  },
}
</script>

<style lang="less">
@import './BasicLayout.less';

/* 直接使用全局样式覆盖，不使用 scoped */
/* 使用更具体的选择器和 !important 提高优先级 */

/* 菜单项激活状态 - 通用样式（包括所有嵌套层级） */
.ant-layout-sider .ant-menu .ant-menu-item-selected,
.ant-layout-sider .ant-menu.ant-menu-inline .ant-menu-item-selected,
.ant-layout-sider .ant-menu.ant-menu-vertical .ant-menu-item-selected,
.ant-layout-sider .ant-menu .ant-menu .ant-menu-item-selected,
.ant-layout-sider .ant-menu .ant-menu .ant-menu .ant-menu-item-selected,
.ant-layout-sider li.ant-menu-item-selected,
.ant-layout-sider li.ant-menu-item-selected > a,
.ant-layout-sider .ant-menu-item-selected > span,
.ant-layout-sider .ant-menu-item-selected > div {
  background-color: #165DFF !important;
  color: #ffffff !important;
}

/* 菜单项激活状态 - 图标颜色 */
.ant-layout-sider .ant-menu .ant-menu-item-selected .anticon,
.ant-layout-sider .ant-menu.ant-menu-inline .ant-menu-item-selected .anticon,
.ant-layout-sider .ant-menu.ant-menu-inline .ant-menu-item-selected img {
  color: #ffffff !important;
}

/* 菜单项激活状态 - 悬停 */
.ant-layout-sider .ant-menu .ant-menu-item-selected:hover,
.ant-layout-sider .ant-menu.ant-menu-inline .ant-menu-item-selected:hover {
  background-color: #165DFF !important;
  color: #ffffff !important;
}

/* 移除 Ant Design 默认的选中边框 */
.ant-layout-sider .ant-menu .ant-menu-item-selected::after,
.ant-layout-sider .ant-menu.ant-menu-inline .ant-menu-item-selected::after {
  display: none !important;
  opacity: 0 !important;
}

/* 浅色主题 - 激活状态 */
.ant-layout-sider .ant-menu-light .ant-menu-item-selected,
.ant-layout-sider .ant-menu-light.ant-menu-inline .ant-menu-item-selected {
  background-color: #165DFF !important;
  color: #ffffff !important;
}

.ant-layout-sider .ant-menu-light .ant-menu-item-selected .anticon,
.ant-layout-sider .ant-menu-light .ant-menu-item-selected img {
  color: #ffffff !important;
}

/* 深色主题 - 激活状态 */
.ant-layout-sider .ant-menu-dark .ant-menu-item-selected,
.ant-layout-sider .ant-menu-dark.ant-menu-inline .ant-menu-item-selected {
  background-color: #165DFF !important;
  color: #ffffff !important;
}

.ant-layout-sider .ant-menu-dark .ant-menu-item-selected .anticon,
.ant-layout-sider .ant-menu-dark .ant-menu-item-selected img {
  color: #ffffff !important;
}

/* 子菜单中的激活项 - 更具体的选择器 */
.ant-layout-sider .ant-menu-submenu .ant-menu-item-selected,
.ant-layout-sider .ant-menu-submenu-inline .ant-menu-item-selected,
.ant-layout-sider .ant-menu-submenu .ant-menu .ant-menu-item-selected,
.ant-layout-sider .ant-menu-submenu .ant-menu-inline .ant-menu-item-selected,
.ant-layout-sider .ant-menu .ant-menu-submenu .ant-menu-item-selected,
.ant-layout-sider .ant-menu-inline .ant-menu-submenu .ant-menu-item-selected,
.ant-layout-sider .ant-menu-submenu .ant-menu-submenu .ant-menu-item-selected,
.ant-layout-sider .ant-menu-submenu li.ant-menu-item-selected,
.ant-layout-sider .ant-menu-submenu li.ant-menu-item-selected > a,
.ant-layout-sider .ant-menu-submenu .ant-menu-item-selected > span,
.ant-layout-sider .ant-menu-submenu .ant-menu-item-selected > div,
.ant-layout-sider .ant-menu-submenu .ant-menu-item.ant-menu-item-selected,
.ant-layout-sider .ant-menu-submenu .ant-menu-item.ant-menu-item-selected > span,
.ant-layout-sider .ant-menu-submenu .ant-menu-item.ant-menu-item-selected > a,
.ant-layout-sider .ant-menu-submenu .ant-menu-item.ant-menu-item-selected > div,
.ant-layout-sider .ant-menu-submenu ul .ant-menu-item-selected,
.ant-layout-sider .ant-menu-submenu ul li.ant-menu-item-selected {
  background-color: #165DFF !important;
  background: #165DFF !important;
  color: #ffffff !important;
}

.ant-layout-sider .ant-menu-submenu .ant-menu-item-selected .anticon,
.ant-layout-sider .ant-menu-submenu .ant-menu-item-selected img,
.ant-layout-sider .ant-menu-submenu .ant-menu .ant-menu-item-selected .anticon,
.ant-layout-sider .ant-menu-submenu .ant-menu .ant-menu-item-selected img,
.ant-layout-sider .ant-menu .ant-menu-submenu .ant-menu-item-selected .anticon,
.ant-layout-sider .ant-menu .ant-menu-submenu .ant-menu-item-selected img {
  color: #ffffff !important;
}

/* 子菜单激活项悬停 */
.ant-layout-sider .ant-menu-submenu .ant-menu-item-selected:hover,
.ant-layout-sider .ant-menu-submenu .ant-menu .ant-menu-item-selected:hover {
  background-color: #165DFF !important;
  color: #ffffff !important;
}

/* 移除子菜单激活项的边框 */
.ant-layout-sider .ant-menu-submenu .ant-menu-item-selected::after,
.ant-layout-sider .ant-menu-submenu .ant-menu .ant-menu-item-selected::after {
  display: none !important;
  opacity: 0 !important;
}

/* 菜单项通用样式 */
.ant-layout-sider .ant-menu-item,
.ant-layout-sider .ant-menu-submenu-title {
  border-radius: 4px !important;
  margin: 2px 8px !important;
  transition: all 0.2s ease !important;
}

/* 强制激活状态样式 - 使用最高优先级 */
.ant-layout-sider .ant-menu-item.ant-menu-item-selected,
.ant-layout-sider .ant-menu-item.ant-menu-item-selected > span,
.ant-layout-sider .ant-menu-item.ant-menu-item-selected > a,
.ant-layout-sider .ant-menu-item.ant-menu-item-selected > div {
  background-color: #165DFF !important;
  background: #165DFF !important;
  color: #ffffff !important;
}

/* 子菜单中的激活项 - 最高优先级（放在最后，优先级最高） */
.ant-layout-sider .ant-menu-submenu .ant-menu-item.ant-menu-item-selected,
.ant-layout-sider .ant-menu-submenu .ant-menu-item.ant-menu-item-selected > span,
.ant-layout-sider .ant-menu-submenu .ant-menu-item.ant-menu-item-selected > a,
.ant-layout-sider .ant-menu-submenu .ant-menu-item.ant-menu-item-selected > div,
.ant-layout-sider .ant-menu-submenu ul .ant-menu-item.ant-menu-item-selected,
.ant-layout-sider .ant-menu-submenu ul li.ant-menu-item.ant-menu-item-selected,
.ant-layout-sider .ant-menu-submenu-inner .ant-menu-item.ant-menu-item-selected {
  background-color: #165DFF !important;
  background: #165DFF !important;
  color: #ffffff !important;
}

/* 修复侧边栏横向滚动条问题 */
.ant-layout-sider {
  overflow-x: hidden !important;
  
  .ant-layout-sider-children {
    overflow-x: hidden !important;
    width: 100% !important;
  }
  
  .ant-menu {
    overflow-x: hidden !important;
    width: 100% !important;
    max-width: 100% !important;
    
    .ant-menu-item,
    .ant-menu-submenu-title {
      max-width: 100% !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      white-space: nowrap !important;
      padding-left: 16px !important;
      padding-right: 16px !important;
      box-sizing: border-box !important;
      
      > span {
        max-width: 100% !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        display: inline-block !important;
      }
    }
    
    .ant-menu-submenu {
      max-width: 100% !important;
      overflow: hidden !important;
      
      .ant-menu {
        max-width: 100% !important;
        overflow-x: hidden !important;
        padding-left: 0 !important;
      }
      
      /* 子菜单项与父菜单文字对齐 */
      .ant-menu .ant-menu-item,
      .ant-menu-submenu .ant-menu .ant-menu-item {
        padding-left: 44px !important; /* 16px(padding) + 16px(图标) + 12px(间距) = 44px，与父菜单文字对齐 */
        padding-right: 16px !important;
      }
      
      /* 二级子菜单项 */
      .ant-menu .ant-menu-submenu .ant-menu-item,
      .ant-menu-submenu .ant-menu .ant-menu-submenu .ant-menu-item {
        padding-left: 72px !important; /* 44px + 16px(图标) + 12px(间距) = 72px */
      }
      
      /* 子菜单中的子菜单标题 */
      .ant-menu .ant-menu-submenu-title,
      .ant-menu-submenu .ant-menu .ant-menu-submenu-title {
        padding-left: 44px !important;
        padding-right: 16px !important;
      }
    }
    
    /* 折叠状态下优化 - 隐藏文字，不显示省略号 */
    &.ant-menu-inline-collapsed {
      .ant-menu-item,
      .ant-menu-submenu-title {
        text-overflow: clip !important;
        overflow: visible !important;
        
        > span {
          display: none !important;
        }
        
        /* 确保图标居中显示 */
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
      
      .ant-menu-submenu-title {
        > span {
          display: none !important;
        }
      }
    }
  }
}
</style>